'use client'

import { useRef, useState } from 'react'
import { LuCheck } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import Spinner from '@/core/components/spinner'
import { cn } from '@/core/utils/common'
import RewardIcon from '@/features/veluvit/components/attendance/reward-icon'
import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import { MidnightCountdown } from '@/features/veluvit/components/ui/atoms'
import { attendDays, attendReward } from '@/features/veluvit/lib/attendance'
import { fmt } from '@/features/veluvit/lib/format'
import { refreshSession } from '@/features/veluvit/services/session.service'
import { claimAttendance } from '@/features/veluvit/services/wallet.actions'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

type AttendanceScreenProps = { modal?: boolean }

export default function AttendanceScreen({
  modal = false,
}: AttendanceScreenProps) {
  const attendance = useSessionStore((state) => state.data?.attendance)
  const stamped = useUiStore((state) => state.justStamped)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const locked = useRef(false)
  const days = attendDays(attendance?.rewards, [])
  const claimed = attendance?.claimed === true
  const day = attendance?.day ?? 1
  const done = claimed ? day : day - 1
  const today = days.find((item) => item.d === day)
  const reward = today ? attendReward(today) : null

  const reload = async () => {
    if (locked.current) return
    locked.current = true
    setPending(true)
    setError('')
    try {
      await refreshSession()
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : '출석 정보를 불러오지 못했어요.'
      )
    } finally {
      locked.current = false
      setPending(false)
    }
  }
  const claim = async () => {
    if (locked.current || claimed || !today) return
    locked.current = true
    setPending(true)
    setError('')
    try {
      await claimAttendance()
    } finally {
      locked.current = false
      setPending(false)
    }
  }

  return (
    <>
      {modal ? (
        <SheetHead title="출석 체크" />
      ) : (
        <PageHeader back title="출석 체크" />
      )}
      <div className={cn('pb-1', !modal && 'mx-auto max-w-120 px-5 pb-8')}>
        {reward ? (
          <>
            <div className="relative flex min-h-32 items-center gap-3 border-b border-border pb-5">
              <div className="min-w-0 flex-1">
                <p className="text-c13 text-t3">
                  {claimed
                    ? '오늘도 함께해 줘서 고마워요'
                    : '매일 만나는 작은 선물'}
                </p>
                <h2 className="mt-2 text-c24 font-bold leading-tight tracking-tight over10:text-c28">
                  {claimed ? '오늘 출석 완료' : '오늘의 보상을 받아요'}
                </h2>
                <p className="mt-2 flex items-baseline gap-1.5 text-c16 font-semibold text-gem">
                  {claimed && (
                    <LuCheck
                      aria-hidden="true"
                      className="size-4 self-center"
                    />
                  )}
                  {reward.kind === 'gem'
                    ? '+' + fmt(reward.amount) + ' 젬'
                    : '해금권 ' + reward.amount + '장'}
                  <span className="text-c13 font-normal text-t3">
                    {claimed ? '받았어요' : day + '일차 보상'}
                  </span>
                </p>
              </div>
              <RewardIcon
                kind={reward.kind}
                art
                px={128}
                className="size-20 flex-none object-contain over10:size-24"
              />
            </div>
            <div className="mt-5 flex items-center justify-between gap-3">
              <h3 className="text-c15 font-semibold">7일 출석 선물</h3>
              <span className="text-c13 tabular-nums text-t3">
                <b className="text-t1">{done}</b> / {days.length}일
              </span>
            </div>
            <ol aria-label="출석 보상" className="mt-3 grid grid-cols-4 gap-2">
              {days.map((item, index) => {
                const itemReward = attendReward(item)
                const received = item.d <= done
                const current = item.d === day && !claimed
                const final = index === days.length - 1
                return (
                  <li
                    key={item.d}
                    aria-current={current ? 'date' : undefined}
                    aria-label={`${item.d}일차 ${itemReward.label} ${fmt(itemReward.amount)}${received ? ', 받음' : current ? ', 오늘 받을 수 있음' : ''}`}
                    className={cn(
                      'relative flex min-w-0 flex-col items-center justify-center gap-2 rounded-panel px-1.5 py-3 text-center',
                      final && 'col-start-4 row-span-2 row-start-1',
                      final && !received && !current && 'bg-sub/8',
                      current && 'bg-point/10',
                      received && 'bg-foreground/[0.03]'
                    )}
                  >
                    <span
                      className={cn(
                        'text-c12 font-medium',
                        current ? 'text-point' : 'text-t3'
                      )}
                    >
                      {current ? '오늘' : item.d + '일차'}
                    </span>
                    {final && (
                      <span className="text-c12 font-semibold text-sub">
                        최종 선물
                      </span>
                    )}
                    <RewardIcon
                      kind={itemReward.kind}
                      art
                      px={128}
                      className={cn(
                        'object-contain',
                        final ? 'my-2 size-14' : 'size-10',
                        received && 'opacity-35'
                      )}
                    />
                    <span
                      className={cn(
                        'text-c13 font-semibold tabular-nums',
                        final && !received && 'text-sub',
                        received && 'text-t3'
                      )}
                    >
                      {fmt(itemReward.amount)}
                      {itemReward.kind === 'ticket' ? '장' : ''}
                    </span>
                    {received && (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute inset-0 grid place-items-center',
                          stamped === item.d && 'animate-stamp-slam'
                        )}
                      >
                        <span className="grid size-9 place-items-center rounded-full bg-background/70 text-point backdrop-blur-sm">
                          <LuCheck className="size-5" strokeWidth={3} />
                        </span>
                      </span>
                    )}
                  </li>
                )
              })}
            </ol>
            <div className="mt-5 border-t border-border pt-5">
              {claimed ? (
                <div
                  role="status"
                  className="flex min-h-12 items-center justify-between gap-3 text-c14"
                >
                  <span className="font-medium text-t2">다음 선물까지</span>
                  <span className="font-semibold tabular-nums">
                    <MidnightCountdown />
                  </span>
                </div>
              ) : (
                <Button
                  variant="primary"
                  size="lg"
                  bold
                  className="w-full"
                  loading={pending}
                  onClick={() => void claim()}
                >
                  출석하기
                </Button>
              )}
              <p className="mt-3 text-center text-c12 leading-5 text-t3">
                매일 00:00에 새로 받을 수 있어요.
                <br />
                출석 젬은 받은 날부터 7일 동안 사용할 수 있어요.
              </p>
            </div>
          </>
        ) : (
          <div className="space-y-4 py-8 text-center">
            {!attendance && (
              <div className="flex justify-center">
                <Spinner />
              </div>
            )}
            <p className="text-c14 text-t3">
              출석 보상 정보를 확인하고 있어요.
            </p>
            <Button
              variant="secondary"
              loading={pending}
              onClick={() => void reload()}
            >
              다시 불러오기
            </Button>
          </div>
        )}
        {error && (
          <p role="alert" className="mt-3 text-c14 text-error">
            {error}
          </p>
        )}
      </div>
    </>
  )
}
